/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, setAuthUserId } from '../lib/supabase';
import { ensureAppIdentity, getRoleForUser, storePendingSignup } from '../services/authBootstrapService';
import { setSentryUser } from '../lib/sentry';
import { identifyUser as identifyPostHogUser, resetUser as resetPostHogUser, trackEvent } from '../services/posthog';

const AuthContext = createContext(null);

// Phase 11 slot 14 — June → Juuni AI rebrand. Storage keys migrated:
//   sessionStorage: june_impersonating → juuni_impersonating
//   localStorage:   june_god_mode → juuni_god_mode
//                   june_selected_org_id → juuni_selected_org_id
// On first read of each new key, transparently migrate any legacy `june_*`
// value if present. Cleared after one read so dev/staging users don't lose
// their state across the rename.
function migrateLegacyStorageKey(storage, oldKey, newKey) {
  try {
    const existing = storage.getItem(newKey);
    if (existing !== null) return;
    const legacy = storage.getItem(oldKey);
    if (legacy === null) return;
    storage.setItem(newKey, legacy);
    storage.removeItem(oldKey);
  } catch { /* storage blocked (safari private, quota, etc.) — non-critical */ }
}

function readStoredImpersonation() {
  try {
    migrateLegacyStorageKey(sessionStorage, 'june_impersonating', 'juuni_impersonating');
    const raw = sessionStorage.getItem('juuni_impersonating');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // God mode state (persisted in localStorage)
  // Phase 11 slot 14: legacy `june_god_mode` key migrated to `juuni_god_mode`
  // on first read so dev/staging users keep their toggle across the rename.
  const [godModeEnabled, setGodModeEnabled] = useState(() => {
    try {
      migrateLegacyStorageKey(localStorage, 'june_god_mode', 'juuni_god_mode');
      return localStorage.getItem('juuni_god_mode') === 'true';
    } catch { return false; }
  });

  // Impersonation state
  const [impersonating, setImpersonating] = useState(() => readStoredImpersonation()); // { id, name, email, role, sourceUserId }
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    try {
      migrateLegacyStorageKey(localStorage, 'june_selected_org_id', 'juuni_selected_org_id');
      return localStorage.getItem('juuni_selected_org_id') || null;
    } catch { return null; }
  });
  const sessionSyncRef = useRef(new Map());

  const syncSession = useCallback(async (session) => {
    if (!session?.user) {
      setUser(null);
      setAuthUserId(null);
      setSentryUser(null);
      resetPostHogUser(); // fire-and-forget; never blocks logout
      setLoading(false);
      return null;
    }

    setLoading(true);
    const existingSync = sessionSyncRef.current.get(session.user.id);
    if (!existingSync) {
      sessionSyncRef.current.set(session.user.id, (async () => {
        let nextUser = session.user;

        try {
          const bootstrap = await ensureAppIdentity(nextUser);
          if (bootstrap?.refreshed) {
            const { data: { session: refreshedSession } } = await supabase.auth.getSession();
            nextUser = refreshedSession?.user || nextUser;
          }
        } catch (error) {
          console.warn('Auth bootstrap failed:', error.message);
        }

        return nextUser;
      })());
    }

    try {
      const nextUser = await sessionSyncRef.current.get(session.user.id);
      setUser(nextUser);
      setAuthUserId(nextUser?.id || null);
      // Attach signed-in identity to every Sentry report (id + email only — no PII beyond that)
      setSentryUser(nextUser ? { id: nextUser.id, email: nextUser.email } : null);

      // Product analytics — identify in PostHog with non-PII properties + group by org.
      // Fires "signup_completed" the first time we see a user whose
      // auth.user was created in the last 30s (covers all signup paths:
      // email/password, OAuth, magic link — regardless of which file
      // initiated the signup).
      if (nextUser?.id) {
        const role = nextUser?.app_metadata?.role
          || nextUser?.user_metadata?.role
          || 'unknown';
        const orgId = nextUser?.app_metadata?.org_id || null;
        const createdAt = nextUser?.created_at || null;
        const signupSource = nextUser?.app_metadata?.provider
          || nextUser?.app_metadata?.providers?.[0]
          || 'email';

        identifyPostHogUser({
          userId: nextUser.id,
          role,
          orgId,
          signupSource,
          createdAt,
        });

        if (createdAt) {
          const ageMs = Date.now() - new Date(createdAt).getTime();
          if (ageMs < 30_000 && ageMs >= 0) {
            trackEvent('signup_completed', {
              signup_source: signupSource,
              has_org: !!orgId,
            });
          }
        }
      }

      return nextUser;
    } finally {
      sessionSyncRef.current.delete(session.user.id);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      syncSession(session);
    }).catch((err) => {
      console.error('Auth session init failed:', err?.message || err);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      syncSession(session);
      if (event === 'SIGNED_OUT' && !session) {
        console.warn('[Auth] Session expired or user signed out');
        // Redirect to login after brief delay so any in-flight requests complete
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.dispatchEvent(new CustomEvent('juuni:error', {
            detail: { message: 'Session expired. Redirecting to login...' },
          }));
          setTimeout(() => { window.location.href = '/login'; }, 2000);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [syncSession]);

  useEffect(() => {
    try {
      if (impersonating) sessionStorage.setItem('juuni_impersonating', JSON.stringify(impersonating));
      else sessionStorage.removeItem('juuni_impersonating');
    } catch {
      /* intentionally empty */
    }
  }, [impersonating]);

  // ─── Role extraction ───────────────────────────────────
  const role = getRoleForUser(user);
  const effectiveRole = impersonating?.role || role;
  const isAdmin = role === 'admin' || role === 'god';
  const isSPL = role === 'spl';
  const isAnnotator = role === 'annotator';
  const isGodMode = role === 'god' || (isAdmin && godModeEnabled);
  const orgId = user?.app_metadata?.org_id || null;

  useEffect(() => {
    if (!impersonating) return;
    if (!user?.id || !['admin', 'god'].includes(role) || impersonating.sourceUserId !== user.id) {
      setImpersonating(null);
      setAuthUserId(user?.id || null);
    }
  }, [impersonating, role, user?.id]);

  // ─── God mode toggle ──────────────────────────────────
  const toggleGodMode = useCallback(() => {
    setGodModeEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('juuni_god_mode', String(next)); } catch { /* intentionally empty */ }
      if (!next && role !== 'god') {
        setSelectedOrgId(null);
        try { localStorage.removeItem('juuni_selected_org_id'); } catch { /* intentionally empty */ }
      }
      return next;
    });
  }, [role]);

  // ─── Impersonation ────────────────────────────────────
  const startImpersonating = useCallback((targetUser) => {
    // targetUser: { id, name, email, role, org_id }
    setImpersonating({ ...targetUser, sourceUserId: user?.id || null });
    setAuthUserId(targetUser.id); // override the cached auth user ID
    // Audit: impersonation is a god-only, high-risk action. Log who started
    // impersonating whom. Dynamic import to keep AuthContext import graph small.
    import('../services/auditService').then(({ logAudit, AUDIT_ACTIONS }) => {
      logAudit(AUDIT_ACTIONS.IMPERSONATE_START, {
        entityType: 'individual',
        entityId: targetUser.id,
        details: {
          source_user_id: user?.id || null,
          source_email: user?.email || null,
          target_user_id: targetUser.id,
          target_email: targetUser.email,
          target_role: targetUser.role,
          target_org_id: targetUser.org_id,
        },
      });
    }).catch(() => { /* fire-and-forget; audit must never block */ });
  }, [user?.id, user?.email]);

  const stopImpersonating = useCallback(() => {
    setImpersonating(null);
    setAuthUserId(user?.id || null); // restore real user
  }, [user]);

  const switchEffectiveOrg = useCallback((nextOrgId) => {
    if (role !== 'god') return;
    const normalized = nextOrgId || null;
    setSelectedOrgId(normalized);
    try {
      if (normalized) localStorage.setItem('juuni_selected_org_id', normalized);
      else localStorage.removeItem('juuni_selected_org_id');
    } catch {
      /* intentionally empty */
    }
  }, [role]);

  // ─── Auth actions ─────────────────────────────────────
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    const syncedUser = await syncSession(data.session);
    return { ...data, user: syncedUser || data.user };
  };

  const signInWithGoogle = async ({ redirectPath = '/admin', pendingSignup = null } = {}) => {
    if (pendingSignup) {
      storePendingSignup(pendingSignup);
    }
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + redirectPath }
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    setImpersonating(null);
    setSelectedOrgId(null);
    setGodModeEnabled(false);
    try { sessionStorage.removeItem('juuni_impersonating'); } catch { /* intentionally empty */ }
    try { localStorage.removeItem('juuni_god_mode'); } catch { /* intentionally empty */ }
    try { localStorage.removeItem('juuni_selected_org_id'); } catch { /* intentionally empty */ }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{
      // Auth state
      user, loading,

      // Role
      role, isAdmin, isSPL, isAnnotator, isGodMode, orgId,

      // God mode
      godModeEnabled, toggleGodMode,

      // Impersonation
      impersonating, startImpersonating, stopImpersonating,
      selectedOrgId, switchEffectiveOrg,

      // Effective identity (impersonated or real)
      effectiveUserId: impersonating?.id || user?.id || null,
      effectiveRole,
      effectiveOrgId: impersonating?.org_id || (role === 'god' ? selectedOrgId || orgId : orgId),

      // Actions
      signIn, signInWithGoogle, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
